import { useEffect, useReducer, createContext } from 'react'
import { useHistory } from '@truepill/tpos-react-router'
import type { ChildProps } from 'types'

interface Funcs {
  togglePane: (paneType: PaneType) => void
  showPane: (paneType: PaneType) => void
  hidePane: (paneType: PaneType) => void
  setFirstLoad: (newFirstLoad: boolean) => void
}

export interface PaneProviderProps extends Funcs {
  paneType: PaneType
  paneVisible: boolean
  firstLoad: boolean
}

export const PaneContext = createContext<Partial<PaneProviderProps>>({
  paneVisible: false,
  firstLoad: true,
})

export type PaneType = 'Message' | 'Image' | 'Note' | 'Attachments' | 'BulkAction'

interface PaneReducerState {
  visible: boolean
  paneType: PaneType
  firstLoad: boolean
}

type PaneAction = {
  type: 'hide' | 'show' | 'toggle' | 'setFirstLoad'
  paneType: PaneType
}

const PaneReducer = (state: PaneReducerState, action: PaneAction) => {
  const { visible, paneType: currentPaneType } = state
  const { type, paneType } = action

  switch (type) {
    case 'hide':
      return { visible: false, paneType, firstLoad: false }
    case 'toggle':
      // Toggling from a different pane type should always show the pane
      // otherwise no change
      if (paneType === currentPaneType) {
        return { visible: !visible, paneType, firstLoad: false }
      } else {
        return { visible: true, paneType, firstLoad: false }
      }
    case 'show':
      return { visible: true, paneType, firstLoad: false }
    case 'setFirstLoad':
      return { ...state, firstLoad: true }
    default:
      return state
  }
}

const PaneProvider = ({ children }: ChildProps): JSX.Element => {
  const history = useHistory()
  const [{ visible, paneType, firstLoad }, dispatch] = useReducer(PaneReducer, {
    visible: false,
    paneType: 'Message',
    firstLoad: true,
  })

  useEffect(() => {
    history.listen(() => {
      dispatch({ type: 'setFirstLoad', paneType: 'Message' })
    })
  }, [history, dispatch])

  const value = {
    paneType,
    paneVisible: visible,
    firstLoad,
    togglePane: (paneType: PaneType) => {
      dispatch({ type: 'toggle', paneType })
    },
    showPane: (paneType: PaneType) => {
      dispatch({ type: 'show', paneType })
    },
    hidePane: (paneType: PaneType) => {
      dispatch({ type: 'hide', paneType })
    },
  }

  return <PaneContext.Provider value={value}>{children}</PaneContext.Provider>
}

export default PaneProvider
